import React, { useState, useEffect } from 'react';
import { Button as ANTButton, Form, Checkbox, Switch, Space, notification, Radio } from 'antd';
import Select from 'components/Atom/Select';
import DateTimePicker from 'components/Atom/DateTimePicker';
import Input from 'components/Atom/Input';
import { Button } from 'components/Atom/Button';
import { useSelector } from 'react-redux';
import { addSubscription, updateSubscription, getAllProducts, getTokenList } from 'api/subscriptions';
import { TimezoneNames, SUB_CurrencyNames } from 'utils/services';
import { TIMEZONES } from 'utils/constant';
import { PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Option } from 'antd/lib/mentions';

const NAME_VALIDATION_REGEX = /^[^%?{}[\]$^*@<>]*$/;
const NAME_VALIDATION_MESSAGE = '%?{}[]$^*@<> are not allowed.';

const validateName = (_, value) => {
  if (!value) return Promise.resolve();
  if (NAME_VALIDATION_REGEX.test(value?.trim())) return Promise.resolve();
  return Promise.reject(NAME_VALIDATION_MESSAGE);
};

const CreateSubscription = ({ handleOpen, refetch, subscriptionData, isEdit }) => {
  const init_Product = subscriptionData?.product?.map((product) => ({
    label: `${product?.name}  / ${product?.product_id}`,
    value: product?.product_id,
    quantity: product?.quantity,
    price: product?.price,
  }));
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProducts] = useState(init_Product?.map((item) => item?.value) || []);
  const store = useSelector((state) => state?.company?.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  const [timezone, setTimezone] = useState(TIMEZONES?.PKT);
  const [collectAdvance, setCollectAdvance] = useState(false);
  const [retryValues, setRetryValues] = useState(!isEdit ? [''] : subscriptionData?.payment_retries_timing?.map((item) => `${item?.date}-${item?.hour}-${item?.minute}`));
  const [isSubscriptionAdding, setIsSubscriptionAdding] = useState(false);
  const [allowedRetries, setAllowedRetries] = useState(!isEdit ? store?.subscription?.allowed_retries : subscriptionData?.admin_settings?.allowed_retries || 0);
  const [form] = Form?.useForm();
  const [planID, setPlanID] = useState(true);
  const [updatePrice, setUpdatePrice] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [delayTimer, setDelayTimer] = useState();
  const [paymentToken, setpaymentToken] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isEdit) return;
      const params = { ...(store?.isSelected && { store_id: store?._id }), active: true, xap_request: true };
      const response = await getAllProducts(params);
      const modifiedData = response?.data?.data?.products?.map((product) => {
        const productObj = {
          label: `${product?.name}  / ${product?.product_id}`,
          value: product?.product_id,
        };
        return productObj;
      });
      modifiedData && setProducts(modifiedData);
    })();
  }, [store]);

  const handleRadioChange = (e) => {
    setPlanID(e.target.value);
  };
  const handlePriceChange = (e) => {
    setUpdatePrice(e?.target?.checked);
  };
  const handleTimezone = (e) => {
    setTimezone(e?.value);
  };

  const handleCollectAdvanceChange = (checked) => {
    setCollectAdvance(checked);
  };

  const extractDateComponents = (timing) => {
    const [date, hour, minute] = timing?.split('-')?.map(Number);
    return {
      date,
      hour,
      minute,
    };
  };

  const filterEmptyObjects = (obj) => {
    const isObject = (val) => val && typeof val === 'object' && !Array?.isArray(val);

    return Object?.fromEntries(
      Object?.entries(obj)
        ?.filter(([key, value]) => {
          if (Array?.isArray(value)) {
            return value?.length > 0;
          }
          if (isObject(value)) {
            const filteredValue = filterEmptyObjects(value);
            return Object?.keys(filteredValue)?.length > 0;
          }
          return value !== undefined && value !== null;
        })
        ?.map(([key, value]) => [key, isObject(value) ? filterEmptyObjects(value) : value]),
    );
  };

  const onAddSubmit = async (payload) => {
    if (userStores?.length > 1 && !store?.isSelected && !isEdit) {
      notification.error({ message: 'Select store from the drop-down menu above', placement: 'bottomRight' });
      return;
    }
    if (Object?.keys(payload)?.length === 0) {
      notification.error({ message: 'Please make any change to update subscription', placement: 'bottomRight' });
      return;
    }
    setIsSubscriptionAdding(true);

    const response = isEdit ? await updateSubscription(subscriptionData?.store_id, subscriptionData?.subscription_id, payload) : await addSubscription(store?._id, payload);
    if (response?.data?.success) {
      handleOpen();
      refetch();
    }
    setIsSubscriptionAdding(false);
  };
  const initialValues = isEdit
    ? {
        name: subscriptionData?.name,
        plan_id: subscriptionData?.plan_id,
        amount: subscriptionData?.payment?.amount,
        payment_token: subscriptionData?.payment?.token,
        currency: { label: subscriptionData?.payment?.currency, value: subscriptionData?.payment?.currency },
        timezone: { label: subscriptionData?.timezone, value: subscriptionData?.timezone },
        interval: { label: subscriptionData?.billing_cycle?.interval, value: subscriptionData?.billing_cycle?.interval },
        every: subscriptionData?.billing_cycle?.every,
        payment_retries_timing: subscriptionData?.payment_retries_timing?.map((item) => `${item?.date}-${item?.hour}-${item?.minute}`),
        allowed_retries: subscriptionData?.admin_settings?.allowed_retries,
        products: init_Product,
        latest_product_price: updatePrice,
      }
    : {
        timezone: timezone,
        failure_action: { value: 'CANCEL', label: 'CANCEL' },
        payment_retries_timing: [''],
      };

  const handleSubmit = async (values) => {
    let payload = {};
    const productArray = values?.products
      ?.filter((item) => item?.selectedProduct?.value || item?.value)
      .map((item) => ({
        product_id: item?.selectedProduct?.value || item?.value,
        quantity: Number(item?.quantity) || 1,
        ...(!updatePrice && { price: Number(item?.price) }),
        name: item?.selectedProduct?.label?.split('  / ')[0],
      }));

    if (isEdit) {
      const updatedPayload = {
        ...(values?.name?.trim() && { name: values?.name?.trim() }),
        ...(planID && { plan_id: values?.plan_id }),
        payment: {
          ...(!planID && updatePrice && { amount: Number(values?.amount) }),
          ...(values?.payment_token && values?.payment_token !== initialValues.payment_token && { token: values?.payment_token?.value }),
          ...(!planID && { currency: values?.currency?.value }),
          ...(updatePrice && !planID && { latest_product_price: updatePrice }),
        },
        timezone: values?.timezone?.value,
        billing_cycle: {
          ...(!planID && { interval: values?.interval?.value }),
          ...(!planID && { every: Number(values?.every) }),
        },
        ...(allowedRetries > 0 && retryValues !== '' && retryValues[0] !== '' && { payment_retries_timing: retryValues?.map(extractDateComponents) }),
        admin_settings: {
          allowed_retries: Number(values?.allowed_retries),
        },
      };
      if (productArray?.length > 0 && !planID) {
        updatedPayload.product = productArray;
      }
      if (collectAdvance) {
        updatedPayload.failure_action = values?.failure_action?.value;
      }

      payload = filterEmptyObjects(updatedPayload);
    } else {
      const newdate = new Date(values?.start_date);
      const timezoneOffset = newdate?.getTimezoneOffset();
      const localISOTime = new Date(newdate?.getTime() - timezoneOffset * 60000)?.toISOString();
      const [date, timeWithMs] = localISOTime?.split('T');
      const [time, msWithZ] = timeWithMs?.split('.');
      const formattedStartDate = `${date}T${time}Z`;

      payload = {
        ...(values?.name?.trim() && { name: values?.name?.trim() }),
        plan_id: values?.plan_id,
        start_date: formattedStartDate,
        payment: {
          token: paymentToken,
          ...(values?.amount && { amount: Number(values?.amount) }),
          ...(values?.amount && { currency: values?.currency?.value || 'PKR' }),
          ...(values?.customer_id && { customer_id: values?.customer_id }),
        },
        timezone: values?.timezone?.value || TIMEZONES?.PKT,
        collect_advance: values?.collect_advance,
        ...(values?.collect_advance && { failure_action: values?.failure_action?.value }),
        ...(retryValues !== '' && retryValues[0] !== '' && { payment_retries_timing: retryValues?.map(extractDateComponents) }),
        product: productArray,
      };
    }

    await onAddSubmit(payload);
  };

  const failureActionOptions = [
    {
      label: 'CANCEL',
      value: 'CANCEL',
    },
    {
      label: 'CONTINUE',
      value: 'CONTINUE',
    },
  ];
  const intervalOptions = [
    {
      label: 'Minute',
      value: 'minute',
    },
    {
      label: 'Day',
      value: 'day',
    },
    {
      label: 'Week',
      value: 'week',
    },
    {
      label: 'Month',
      value: 'month',
    },
    {
      label: 'Year',
      value: 'year',
    },
  ];
  const handleChange = (index, newValue) => {
    const newValues = [...retryValues];
    newValues[index] = newValue;
    setRetryValues(newValues);
  };

  const removeInput = (index) => {
    setRetryValues(retryValues?.filter((_, i) => i !== index));
  };

  const handleSearch = async (searchValue) => {
    if (!searchValue) {
      setOptions(null);
      return;
    }
    setLoading(true);
    try {
      const params = { ...(store?.isSelected && { store_id: store?._id }), search_key: searchValue, enabled: true, xap_request: true };
      const response = await getTokenList(params);
      const tokens = response?.data?.data?.docs;
      const newOptions = tokens?.map((token) => {
        const maskedTokenId = token?.payment_method === 'wallet' ? `${token?.wallet_details?.phone}` : `**** **** **** ${token?.card_details?.last_four_digits}`;

        return {
          label: (
            <span style={{ display: 'flex', gap: '8px' }}>
              <img
                src={token?.payment_method === 'wallet' ? `https://js.xstak.com/images/jazzcash.svg` : `https://js.xstak.com/images/${token?.card_details?.card_network?.toLowerCase()}.svg`}
                width={25}
                alt={token?.payment_method === 'wallet' ? `Jazzcash` : `${token?.card_details?.card_network}`}
              />
              {maskedTokenId}
            </span>
          ),
          value: token?._id,
        };
      });
      setOptions(newOptions);
      setDropdownOpen(true);
    } catch (error) {}
    setLoading(false);
  };

  const handleSelectChange = (value) => {
    setpaymentToken(value?.value);
    setDropdownOpen(false);
    setOptions(null);
  };

  const handleTokenChange = (value) => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      const regex = /^[^%&?{}[\]$^<>|\\()/]+$/;
      if (value && !regex.test(value)) {
        notification.error({
          message: 'Validation Error',
          description: '%&?{}[]$^<>|\\()/ are not allowed.',
          placement: 'bottomRight',
        });
      } else {
        handleSearch(value);
      }
    }, 1000);
    setDelayTimer(newTimer);
  };
  const handleRetriesChange = (e) => {
    setAllowedRetries(e?.target?.value);
  };
  const mapRetryInput = (array, remove) => {
    return array?.map((field, index) => (
      <Space key={index} align='start' className='feature-Input'>
        <Form.Item {...field} name={[field?.name]} key={index}>
          <DashInput value={retryValues?.[index]} onChange={(newValue) => handleChange(index, newValue)} />
        </Form.Item>
        {array?.length > 1 ? (
          <MinusCircleOutlined
            style={{ marginTop: '11px' }}
            onClick={() => {
              remove(field?.name);
              removeInput(index);
            }}
          />
        ) : null}
      </Space>
    ));
  };

  return (
    <>
      {isEdit && (
        <div className='radio-button' style={{ marginBottom: '12px' }}>
          <Radio.Group name='radiogroup' onChange={handleRadioChange} value={planID}>
            <Radio style={{ fontWeight: '400' }} value={true}>
              With Plan ID
            </Radio>
            <Radio style={{ fontWeight: '400' }} value={false}>
              Without Plan ID
            </Radio>
          </Radio.Group>
        </div>
      )}
      <div className='formDetails'>
        <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={initialValues}>
          <div className='form-inputs'>
            <div className='create-form'>
              <Form.Item name='name' label='Name' className='customInput mobileInputView' rules={[{ validator: validateName }]}>
                <Input type='string' placeholder='Enter name' name='name' />
              </Form.Item>
              {/* <Form.Item
                name='payment_token'
                label='Payment Token'
                className={`customInput mobileInputView ${!isEdit ? 'steric-after' : ''}`}
                rules={
                  !isEdit && [
                    {
                      required: true,
                      message: 'Payment Token is required',
                    },
                  ]
                }>
                <Input type='string' placeholder='Enter Payment Token' name='payment_token' />
              </Form.Item> */}
              <Form.Item
                name='payment_token'
                label='Payment Token'
                className='customInput payment-token mobileInputView steric-after'
                rules={[{ required: true, message: 'Payment Token is required' }]}>
                <Select
                  showSearch
                  placeholder='Search by Token_id, Email and Phone'
                  onSearch={handleTokenChange}
                  options={options}
                  filterOption={false}
                  loading={loading}
                  onDropdownVisibleChange={(open) => {
                    setDropdownOpen(open);
                  }}
                  open={dropdownOpen}
                  onChange={handleSelectChange}
                  onBlur={() => {
                    setDropdownOpen(false);
                    setOptions(null);
                  }}
                />
              </Form.Item>
              <Form.Item
                name='customer_id'
                label='Customer ID'
                className={`customInput mobileInputView`}
                rules={[
                  !isEdit && {
                    required: false,
                    message: 'Customer ID is optional',
                  },
                  {
                    message: "Invalid Customer ID.Only allow '- _' in the middle.",
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (/^(?![_-]+$)(?!^[_-]+)(?!.*[_-]$)(?!.*--)(?!.*__)[a-zA-Z0-9_-]+$/.test(value)) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Invalid Customer ID');
                      }
                    },
                  },
                ].filter(Boolean)}>
                <Input type='string' placeholder='Enter Customer Id' name='customer_id' />
              </Form.Item>
              <Form.Item
                name='timezone'
                label='Timezone'
                className={`customInput mobileInputView ${!isEdit ? 'steric-after' : ''}`}
                rules={
                  !isEdit && [
                    {
                      required: true,
                      message: 'Timezone is required',
                    },
                  ]
                }>
                <Select handleChange={handleTimezone} defaultValue={timezone} optionsValue={TimezoneNames} />
              </Form.Item>
              {isEdit && planID === false && (
                <>
                  <Form.Item
                    name='every'
                    label='Every'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        message: 'Only positive numbers allowed',
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (/^\d+$/.test(value) && Number(value) > 0) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject('Invalid Every');
                          }
                        },
                      },
                    ]}>
                    <Input type='number' placeholder='Enter Every' name='every' />
                  </Form.Item>

                  <Form.Item name='interval' label='Interval' className='customInput mobileInputView '>
                    <Select defaultValue='Select Interval' optionsValue={intervalOptions} />
                  </Form.Item>
                </>
              )}
              {(!isEdit || (isEdit && updatePrice && planID === false)) && (
                <>
                  <Form.Item
                    name='amount'
                    label='Price'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (/^\d+(\.\d+)?$/.test(value) && value?.length <= 15) {
                            return Promise.resolve();
                          }
                          if (!/^\d+(\.\d+)?$/.test(value)) {
                            return Promise.reject('Only positive numbers are allowed');
                          }
                          if (value.length > 15) {
                            return Promise.reject('Maximum 15 digits are allowed');
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}>
                    <Input type='number' placeholder='Enter Price' name='amount' />
                  </Form.Item>

                  <Form.Item name='currency' label='Currency' className='customInput mobileInputView'>
                    <Select showSearch defaultValue='PKR' optionsValue={SUB_CurrencyNames} />
                  </Form.Item>
                </>
              )}
              {!isEdit && (
                <Form.Item
                  name='start_date'
                  label='Start Date'
                  className='customInput mobileInputView steric-after'
                  rules={[
                    {
                      required: true,
                      message: 'Start Date is required',
                    },
                  ]}>
                  <DateTimePicker />
                </Form.Item>
              )}

              {!isEdit && (
                <>
                  <Form.Item name='collect_advance' label='Collect Advance' valuePropName='checked' initialValue={false} className='customInput mobileInputView'>
                    <Switch defaultChecked onChange={handleCollectAdvanceChange} />
                  </Form.Item>

                  {collectAdvance && (
                    <Form.Item name='failure_action' label='Failure Action' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Failure Action is required' }]}>
                      <Select defaultValue={'CANCEL'} optionsValue={failureActionOptions} />
                    </Form.Item>
                  )}
                </>
              )}
              {planID === true && (
                <Form.Item
                  name='plan_id'
                  label='Plan ID'
                  className={`customInput mobileInputView ${!isEdit ? 'steric-after' : ''}`}
                  rules={[
                    !isEdit && {
                      required: true,
                      message: 'Plan ID is required',
                    },
                    {
                      message: "Invalid Plan ID.Only allow '- _' in the middle.",
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (/^(?![_-]+$)(?!^[_-]+)(?!.*[_-]$)(?!.*--)(?!.*__)[a-zA-Z0-9_-]+$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject('Invalid Plan ID');
                        }
                      },
                    },
                  ].filter(Boolean)}>
                  <Input type='string' placeholder='Enter Plan Id' name='plan_id' />
                </Form.Item>
              )}
              {isEdit && (
                <Form.Item name='allowed_retries' label='Retries (Up to 6 allowed)' className='customInput mobileInputView'>
                  <Input type='number' placeholder='Enter Allowed Retries' name='allowed_retries' onChange={handleRetriesChange} min={0} max={6} />
                </Form.Item>
              )}
            </div>
            {allowedRetries > 0 && (
              <Form.List name='payment_retries_timing' initialValue={['']}>
                {(fields, { add, remove }) => (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '4px' }}>
                      <label className='retry-label'>Payment Retries(Day, Hour, Minute)</label>
                      {fields?.length < allowedRetries ? <PlusCircleOutlined style={{ marginLeft: '10px', fontSize: '15px' }} onClick={() => add()} /> : null}
                    </div>
                    <div className='feature-div'>{fields?.length > 0 ? mapRetryInput(fields, remove) : mapRetryInput([1], remove)}</div>
                  </div>
                )}
              </Form.List>
            )}
            {planID === false && isEdit && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: '14px !important', fontWeight: '700', paddingBottom: '4px' }}>
                    Update Product {!isEdit && <span style={{ color: '#ff4d4f', paddingTop: '1px' }}>*</span>}
                  </label>
                  <Form.Item className='customInput mobileInputView checked-label'>
                    <Checkbox onChange={handlePriceChange} style={{ fontWeight: '500' }}>
                      Fetch Price From Product Collection
                    </Checkbox>
                  </Form.Item>
                </div>
                <Form.List name='products' initialValue={['']}>
                  {(fields, { add, remove }) => (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div className=''>
                        {fields?.map(({ key, name, fieldKey, ...restField }, index) => (
                          <Space key={key} align='baseline' className='feature-Input-div'>
                            <div>
                              {index === 0 && <label style={{ fontSize: '14px !important', fontWeight: '500', paddingBottom: '4px' }}>Product</label>}
                              <Form.Item
                                {...restField}
                                name={[name, 'selectedProduct']}
                                fieldKey={[fieldKey, 'selectedProduct']}
                                className='customInput mobileInputView product-select subscription-products'
                                style={{ marginBottom: '0px' }}
                                rules={[{ required: isEdit ? false : true, message: 'Product is required' }]}>
                                <Select
                                  placeholder='Select Product'
                                  showSearch
                                  defaultValue={isEdit ? init_Product?.[index] : undefined}
                                  onChange={(value) => {
                                    setSelectedProducts((prev) => {
                                      prev[index] = value?.value;
                                      return [...prev];
                                    });
                                  }}>
                                  {products?.map((prod) => {
                                    return (
                                      <Option disabled={selectedProduct?.includes(prod?.value)} value={prod?.value}>
                                        {prod?.label}
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </Form.Item>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'self-start' }} className='minus-icon'>
                              <div>
                                {index === 0 && <label style={{ fontSize: '14px !important', fontWeight: '500', paddingBottom: '4px' }}>Quantity</label>}
                                <Form.Item
                                  {...restField}
                                  name={[name, 'quantity']}
                                  fieldKey={[fieldKey, 'quantity']}
                                  rules={[
                                    {
                                      message: 'Only positive numbers allowed',
                                      validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        if (/^[0-9]+$/.test(value)) {
                                          return Promise.resolve();
                                        } else {
                                          return Promise.reject('Invalid quantity');
                                        }
                                      },
                                    },
                                  ]}>
                                  <Input
                                    min={1}
                                    type='number'
                                    defaultValue={isEdit ? Number(init_Product?.[index]?.quantity) || 1 : 1}
                                    placeholder='Enter Quantity'
                                    className='customInput mobileInputView'
                                    style={{ height: '36px', width: '100%', borderRadius: '8px' }}
                                  />
                                </Form.Item>
                              </div>
                              {!updatePrice && (
                                <div>
                                  {index === 0 && <label style={{ fontSize: '14px !important', fontWeight: '500', paddingBottom: '4px' }}>Price</label>}
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'price']}
                                    fieldKey={[fieldKey, 'price']}
                                    className='customInput mobileInputView'
                                    rules={[
                                      {
                                        validator: (_, value) => {
                                          if (!value) return Promise.resolve();
                                          if (/^\d+(\.\d+)?$/.test(value) && value?.length <= 15) {
                                            return Promise.resolve();
                                          }
                                          if (!/^\d+(\.\d+)?$/.test(value)) {
                                            return Promise.reject('Only positive numbers are allowed');
                                          }
                                          if (value.length > 15) {
                                            return Promise.reject('Maximum 15 digits are allowed');
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ]}>
                                    <Input type='number' placeholder='Enter Price' />
                                  </Form.Item>
                                </div>
                              )}

                              {fields?.length > 1 ? (
                                <MinusCircleOutlined
                                  style={{ paddingTop: index === 0 ? '30px' : '10px', fontSize: '18px' }}
                                  onClick={() => {
                                    remove(name);
                                    const currentProduct = [...selectedProduct];
                                    if (currentProduct?.length > 0) {
                                      const updatedProducts = currentProduct?.filter((_, index) => index !== name);
                                      setSelectedProducts(updatedProducts);
                                    }
                                  }}
                                />
                              ) : null}
                              {name === fields?.length - 1 && <PlusCircleOutlined style={{ paddingTop: index === 0 ? '30px' : '10px', fontSize: '18px' }} onClick={() => add()} />}
                            </div>
                          </Space>
                        ))}
                      </div>
                    </div>
                  )}
                </Form.List>
              </>
            )}
          </div>
          <div className='mb-[-20px] form_button update-button'>
            <Form.Item>
              <div style={{ display: 'flex', justifyContent: 'right' }}>
                <ANTButton
                  block
                  className='rounded-lg'
                  onClick={() => {
                    handleOpen();
                  }}>
                  Cancel
                </ANTButton>
                <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isSubscriptionAdding}>
                  {isEdit ? 'Update' : 'Add'}
                </Button>
              </div>
            </Form.Item>
          </div>
        </Form>
      </div>
    </>
  );
};

const DashInput = ({ value, onChange }) => {
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setError('');
    let inputValue = e?.target?.value?.replace(/[^0-9]/g, '');
    inputValue = inputValue?.replace(/-/g, '');
    if (inputValue?.length > 7) {
      inputValue = inputValue?.substring(0, 7);
    }

    const parts = [];
    if (inputValue?.length <= 3) {
      parts?.push(inputValue);
    } else if (inputValue?.length <= 5) {
      parts?.push(inputValue?.slice(0, 3));
      parts?.push(inputValue?.slice(3));
    } else {
      parts?.push(inputValue?.slice(0, 3));
      parts?.push(inputValue?.slice(3, 5));
      parts?.push(inputValue?.slice(5, 7));
    }
    if (parts?.length > 0 && Number(parts[0]) > 999) {
      setError('Days cannot exceed 999');
      return;
    }
    if (parts?.length > 1 && Number(parts[1]) > 23) {
      setError('Hours cannot exceed 23');
      return;
    }
    if (parts?.length > 2 && Number(parts[2]) > 59) {
      setError('Minutes cannot exceed 59');
      return;
    }

    const formattedValue = parts?.join('-');
    onChange(formattedValue);
  };

  return (
    <div>
      <input type='text' className={`rety-input-field ${error ? 'input-error' : ''}`} placeholder='ddd-hh-mm' value={value} onChange={handleChange} maxLength={9} />
      {error && <div className='error-message'>{error}</div>}
    </div>
  );
};
export default CreateSubscription;
